import React from 'react'
import AceEditor from 'react-ace'
import setReadonly from './functions/AceEditorReadOnly'
import { CSSTransition } from 'react-transition-group'

import 'brace/mode/javascript'
import 'brace/theme/monokai'

class CodeEditorModal extends React.Component {
    constructor(props) {
        super(props)

        this.mainInput = HTMLTextAreaElement
        this.modal = HTMLDivElement

        this.handleOnEditorLoad = this.handleOnEditorLoad.bind(this)
        this.changeCallback = this.changeCallback.bind(this)
        this.handleAceEditorChange = this.handleAceEditorChange.bind(this)
        this.handleActualValueChange = this.handleActualValueChange.bind(this)
        this.handleModalToggle = this.handleModalToggle.bind(this)
        this.handleModalExpand = this.handleModalExpand.bind(this)
        this.handleTransitionEntered = this.handleTransitionEntered.bind(this)

        let {
            value,
            enforceArgs
        } = this.props

        // try to parse the value
        let parsedVal = null
        let parsedValType = null
        if (typeof value === 'string') {
            try {
                parsedVal = JSON.parse(value)
                parsedValType = typeof parsedVal
            } catch (e) {
                parsedValType = 'string'
            }
        }

        // if the parsed value is an object with property "function" and type array, it is valid:
        // Eg. { "function": [ "val", "context", "//function body" ] }
        let funcArgs = Array.isArray(enforceArgs) ? enforceArgs : []
        let funcBody = "return val "
        let funcString = ""
        if (parsedVal && parsedValType === 'object' && parsedVal.hasOwnProperty('function') && Array.isArray(parsedVal.function)) {
            // Use enforceArgs if provided, else use parsed args
            funcArgs = Array.isArray(enforceArgs) ? enforceArgs : parsedVal.function.slice(0, parsedVal.function.length - 1)
            funcBody = parsedVal.function[parsedVal.function.length - 1]
        }

        funcString = `function ${this.props.modalTitle.toLowerCase()}(${funcArgs.toString()}){\n\t${funcBody.trim()}\n}`

        this.state = {
            actualValue: value,
            funcArgs,
            funcString,
            editor: null,
            isModalOpen: false,
            isModalExpanded: false
        }
    }

    changeCallback() {
        // since we are setting the main input value programatically, we need to trigger onChange
        // event manually so Formik can save the changed value
        let funcArray = this.state.funcArgs.slice()
        funcArray.push(this.state.funcBody)
        let actualValue = JSON.stringify({ function: funcArray })

        let nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value").set
        nativeInputValueSetter.call(this.mainInput, actualValue)

        let ev2 = new Event('input', { bubbles: true })
        this.mainInput.dispatchEvent(ev2)
    }

    handleModalToggle() {
        // execute beforeClose prop function if available before closing
        if (this.state.isModalOpen && this.props.beforeClose && typeof this.props.beforeClose === "function") {
            this.props.beforeClose()
        }

        this.setState({ isModalOpen: !this.state.isModalOpen }, () => {
            if (this.state.isModalExpanded) {
                this.handleModalExpand()
            }
        })
    }

    handleModalExpand() {
        this.setState({ isModalExpanded: !this.state.isModalExpanded }, () => {
            if (this.state.editor && this.state.editor.resize) {
                this.state.editor.resize()
            }
        })
    }

    handleAceEditorChange(newValue) {
        let firstBrace = newValue.indexOf('{') + 1
        let lastBrace = newValue.lastIndexOf('}')
        let funcBody = newValue.slice(firstBrace, lastBrace).trim()

        this.setState({
            funcBody,
            funcString: newValue
        }, this.changeCallback)
    }

    handleActualValueChange(event) {
        let actualValue = event.target.value

        this.setState({ actualValue })
        this.props.onChange(event)
    }

    handleOnEditorLoad(editor) {
        let firstBrace = this.state.funcString.indexOf('{') + 1
        let lastBrace = this.state.funcString.lastIndexOf('}') + 1
        let posInEditor = editor.session.doc.indexToPosition(lastBrace)
        setReadonly(editor,
            [[0, 0, 0, firstBrace],
            [posInEditor.row, 0, posInEditor.row, posInEditor.column]]
        )
        this.setState({ editor })
    }

    handleTransitionEntered() {
        if (this.state.editor && this.state.editor.resize) {
            this.state.editor.resize()
        }
    }

    render() {
        // jquery selectors need escaping when it includes dot or brace character
        let safeId = this.props.id && this.props.id.replace(/\[|\]|\./g, '')

        return (
            <div className="code-editor-modal btn-group" role="group">
                <button type="button" className="btn btn-sm btn-warning ml-1" title={this.props.buttonTitle || "edit"} onClick={this.handleModalToggle} data-target={`#${safeId}-modal`} data-backdrop="false">
                    {this.props.buttonText || <i className="fas fa-edit"></i>}
                </button>
                <CSSTransition
                    in={this.state.isModalOpen}
                    appear={true}
                    timeout={500}
                    classNames="fade"
                    onEntered={this.handleTransitionEntered}
                >
                    <div className="modal" id={`${safeId}-modal`} tabIndex="-1" role="dialog" aria-labelledby={`${safeId}-title`} aria-hidden={(this.state.isModalOpen ? "false" : "true")}
                        ref={node => this.modal = node} >
                        <div className={"modal-dialog modal-dialog-centered" + (this.state.isModalExpanded ? " modal-expanded" : "")} role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id={`${safeId}-title`}>{this.props.modalTitle}</h5>
                                    <div className="modal-header-buttons close">
                                        <button type="button" className="btn modal-btn" aria-label="Expand" onClick={this.handleModalExpand}>
                                            {this.state.isModalExpanded ?
                                                <i className="fa fa-compress fa-fw"></i> :
                                                <i className="fa fa-expand fa-fw"></i>
                                            }
                                        </button>
                                        <button type="button" className="btn modal-btn" data-dismiss="modal" aria-label="Close" onClick={this.handleModalToggle}>
                                            <i className="fa fa-times fa-fw"></i>
                                        </button>
                                    </div>
                                </div>
                                <textarea
                                    name={this.props.name}
                                    id={this.props.id}
                                    onChange={this.handleActualValueChange}
                                    value={this.state.actualValue || ""}
                                    ref={node => this.mainInput = node}
                                    hidden
                                />
                                <div className="modal-body row align-items-stretch">
                                    <div className="col align-self-stretch">
                                        {this.state.isModalOpen ?
                                            <AceEditor
                                                mode="javascript"
                                                theme="monokai"
                                                width="100%"
                                                height="100%"
                                                style={{ minHeight: 300 }}
                                                fontSize={14}
                                                showPrintMargin={false}
                                                showGutter={true}
                                                highlightActiveLine={false}
                                                name={this.props.id + "-editor"}
                                                editorProps={{ $blockScrolling: true }}
                                                debounceChangePeriod={500}
                                                value={this.state.funcString}
                                                onLoad={this.handleOnEditorLoad}
                                                onChange={this.handleAceEditorChange}
                                            /> : null
                                        }
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" data-dismiss="modal" onClick={this.handleModalToggle}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </CSSTransition>
            </div>
        )
    }
}

// CodeEditorModal.propTypes = {
//     id: PropTypes.string.isRequired,
//     name: PropTypes.string.isRequired,
//     value: PropTypes.array.isRequired
// }

export default CodeEditorModal


// WEBPACK FOOTER //
// ./src/components/utilities/CodeEditorModal.js